import { useEffect, useRef, type ReactNode } from "react";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  className?: string;
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal({ isOpen, onClose, title, children, className }: ModalProps) {
  const { t } = useTranslation();
  const titleId = "modal-title";
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // Focus dans la modale à l'ouverture (le titre, focusable via tabIndex),
    // restauré au trigger à la fermeture — comportement standard de dialog.
    const previouslyFocused = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();

    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      // Confinement du focus : Tab/Shift+Tab bouclent dans la modale plutôt
      // que d'atteindre le contenu derrière l'overlay.
      if (e.key === "Tab" && panelRef.current) {
        const focusable = Array.from(
          panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", handler);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", handler);
      previouslyFocused?.focus();
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cn(
          // `max-w-lg` n'est le défaut QUE si l'appelant ne fournit pas sa
          // propre largeur : `cn()` (= clsx) concatène les classes sans
          // résoudre les conflits Tailwind, donc `max-w-lg` codé en dur ici
          // + un `max-w-2xl` fourni par l'appelant coexistaient sur le même
          // élément — lequel gagnait dépendait de l'ordre dans la feuille de
          // style compilée, pas de l'intention. Repéré via `className`
          // "toujours étroit malgré le fix" sur la modale Ouvrir un cycle.
          "bg-surface border border-border rounded-lg shadow-lg w-full mx-4 p-6 outline-none",
          className || "max-w-lg"
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <h2 id={titleId} className="text-xs font-semibold tracking-widest uppercase text-text-secondary">
            {title}
          </h2>
          <button
            type="button"
            aria-label={t("common.close")}
            className="p-1.5 rounded hover:bg-gray-200 cursor-pointer text-text-muted transition-colors outline-none shadow-none"
            onClick={onClose}
          >
            <X size={16} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
